// useTrip.ts
// manages the trip data, updates, and fetching(?)

import { create } from "zustand";
import {
  Trip,
  Location,
  ItineraryItem,
  TripInfo,
  LocationUpdateType,
  TripLodging,
  ToDo,
  WaytoraExperienceInitial,
  BookingStatus,
  ViatorProductInitial,
  ViatorProductResponse,
  TripUser,
} from "@/types";
import api from "@/config/axiosConfig";
import useAuthStore from "@/stores/user/authStore";
import { toast } from "@/hooks/use-toast";
import axios from "axios";
import useCurrencyStore from "@/stores/ui/currencyStore";
import {
  getCoordinatesFromPlaceId,
  calculateStartTime,
  addViatorProductToDay,
} from "@/lib/experiences";
import { debounce } from "lodash";
import { getMoreProductDetails } from "@/utils/tripBuilder";
import logger from "@/utils/logger";

interface BaseMetadata {
  itemName?: string;
  image?: string;
}

interface ExperienceMetadata extends BaseMetadata {
  duration?: string;
  rating?: number;
  price?: string;
}

interface AccommodationMetadata extends BaseMetadata {
  checkIn?: string;
  checkOut?: string;
  rating?: number;
}

interface TransportMetadata extends BaseMetadata {
  departureTime?: string;
  arrivalTime?: string;
  type?: string; // flight, train, etc
}

interface Participant {
  id: string;
  name: string;
  waytora_avatar_url: string;
}

interface ParticipantsMetadata {
  participants: Participant[];
  added: boolean;
}

type UpdateMetadata =
  | ExperienceMetadata
  | AccommodationMetadata
  | TransportMetadata
  | ParticipantsMetadata;

interface BookingStats {
  transport: { completed: boolean };
  accommodation: { completed: boolean };
  activities: { completed: number; total: number };
}

interface LocationBudget {
  total: number;
  accommodation: number;
  transport: number;
  activities: number;
}

interface TripStore {
  tripData: Trip | null;
  setTripData: (data: Trip | null) => void;
  tripInfo: TripInfo | null;
  setTripInfo: (info: TripInfo | null) => void;
  tripTodos: ToDo[];
  setTripTodos: (todos: ToDo[]) => void;
  selectedLocation: Location | null;
  setSelectedLocation: (location: Location | null) => void;
  selectedLocationId: string | null;
  lastTripEndDate: Date | null;

  loading: boolean;
  error: Error | null;
  localVersion: number;
  hasAccess: boolean;
  isParticipant: boolean;
  fetchTripData: (tripId: string) => Promise<void>;
  updateTripData: (newData: Partial<Trip>) => void;
  addLocation: (newLocation: Location) => void;
  updateLocation: (id: string, updates: Partial<Location>) => void;
  updateActivityItem: (
    locationId: string,
    date: string,
    activityId: string,
    updates: Partial<ItineraryItem>
  ) => Promise<void>;
  setSelectedLocationId: (id: string | null) => void;
  getSelectedLocation: () => Location | null;
  getItineraryItems: () => { date: string; items: ItineraryItem[] }[];
  getItineraryItemLength: (selectedDay: string, location: Location) => number;
  getChatItineraryItems: () => {
    date: string;
    items: { id: string; name: string; start_time: string }[];
  }[];
  getAdjacentLocations: () => {
    previousLocation: Location | null;
    nextLocation: Location | null;
  };
  selectedDay: string | null;
  previousLocation: Location | null;
  nextLocation: Location | null;
  setPreviousLocation: (location: Location | null) => void;
  setNextLocation: (location: Location | null) => void;
  setSelectedDay: (date: string | null) => void;
  bookingStats: Record<string, BookingStats>;
  forceStatsUpdate: number; // Add this
  bookingStatsVersion: number;
  updateBookingStats: (locationId: string, stats: BookingStats) => void;
  recordView: (tripId: string, duration: number) => Promise<void>;
  recordEngagement: (
    tripId: string,
    type: "view" | "like" | "share" | "unlike",
    metadata: any
  ) => Promise<void>;
  copyTrip: (tripId: string) => Promise<void>;
  createLocationUpdate: (
    waytoraId: string,
    locationId: string,
    type: LocationUpdateType,
    metadata: {
      itemName?: string;
      itemType?: string;
      changes?: Record<string, any>;
      [key: string]: any;
    }
  ) => Promise<void>;
  checkIsAdmin: () => boolean;
  requestAdminAccess: () => Promise<void>;
  isPermissionDialogOpen: boolean;
  setPermissionDialogOpen: (isOpen: boolean) => void;
  selectedLocationBudget: LocationBudget;
  setSelectedLocationBudget: (budget: LocationBudget) => void;
  updateLocationBudgetCategory: (
    locationId: string,
    category: keyof LocationBudget,
    amount: number
  ) => void;
  recalculateLocationBudget: (locationId: string) => void;
  getLocationBudgetBreakdown: (locationId: string) => {
    accommodation: { name: string; cost: number; image?: string }[];
    transport: { name: string; cost: number; image?: string }[];
    activities: { name: string; cost: number; image?: string }[];
  };
  getLocationBudgetPerParticipant: (locationId: string) => {
    participant: Participant;
    accommodationShare: number;
    activitiesShare: number;
    transportShare: number;
    total: number;
  }[];
  updateAccommodationItem: (
    locationId: string,
    updates: Partial<TripLodging>
  ) => Promise<void>;
  resetState: () => void;
  addExperienceToItinerary: (
    experience: WaytoraExperienceInitial,
    selectedDay: string,
    selectedLocationId: string,
    placeLocationDetails?: {
      address: string;
      latitude: number;
      longitude: number;
    } | null
  ) => Promise<void>;
  addViatorExperienceToItinerary: (
    experience: ViatorProductInitial,
    selectedDay: string,
    selectedLocationId: string
  ) => Promise<void>;
  getMoreProductDetails: (
    productCode: string,
    viatorId: string
  ) => Promise<ViatorProductResponse>;
}

const useTripStore = create<TripStore>((set, get) => ({
  tripData: null,
  setTripData: (data: Trip | null) =>
    set({
      tripData: data,
      lastTripEndDate: calculateLastTripEndDate(data?.locations),
    }),
  tripInfo: null,
  setTripInfo: (info: TripInfo | null) => set({ tripInfo: info }),
  tripTodos: [],
  setTripTodos: (todos: ToDo[]) => set({ tripTodos: todos }),
  selectedLocation: null,
  selectedLocationId: null,
  lastTripEndDate: null,
  loading: false,
  error: null,
  localVersion: 0,
  hasAccess: false,
  isParticipant: false,
  selectedDay: null,
  previousLocation: null,
  nextLocation: null,
  setSelectedLocation: (location: Location | null) =>
    set({ selectedLocation: location }),
  setPreviousLocation: (location: Location | null) =>
    set({ previousLocation: location }),
  setNextLocation: (location: Location | null) =>
    set({ nextLocation: location }),
  selectedLocationBudget: {
    total: 0,
    accommodation: 0,
    transport: 0,
    activities: 0,
  },
  setSelectedLocationBudget: (budget: LocationBudget) => {
    logger.log("New location budget:", budget);
    set({ selectedLocationBudget: budget });
  },
  updateLocationBudgetCategory: (
    locationId: string,
    category: keyof LocationBudget,
    amount: number
  ) => {
    set((state) => {
      if (!state.tripData) return state;

      const updatedLocations = state.tripData.locations.map((location) => {
        if (location.id === locationId) {
          const updatedBudget = {
            ...location.budget,
            [category]: amount,
            total:
              category === "total"
                ? amount
                : location.budget.total + (amount - location.budget[category]),
          };
          return {
            ...location,
            budget: updatedBudget,
          };
        }
        return location;
      });

      return {
        tripData: {
          ...state.tripData,
          locations: updatedLocations,
          version: (state.tripData.version || 0) + 1,
        },
        selectedLocationBudget:
          state.selectedLocation?.id === locationId
            ? updatedLocations.find((l) => l.id === locationId)?.budget ||
              state.selectedLocationBudget
            : state.selectedLocationBudget,
      };
    });
  },

  fetchTripData: async (tripId: string) => {
    set({ loading: true, error: null });
    try {
      const response = await api.trips.get(`/${tripId}`, {
        params: {
          currency: useCurrencyStore.getState().selectedCurrency.code,
          include_todo_list: true,
        },
      });
      const responseData = response.data as {
        tripData: Trip;
        tripInfo: TripInfo | null;
        has_access: boolean;
        is_participant: boolean;
        todo_list: ToDo[];
      };

      set({
        hasAccess: responseData.has_access,
        isParticipant: responseData.is_participant,
        lastTripEndDate: calculateLastTripEndDate(
          responseData.tripData.locations
        ),
      });

      if (!responseData.has_access) {
        window.history.back();
        toast({
          title: "Access Denied",
          description: "You do not have access to this trip.",
          variant: "destructive",
        });
        return;
      }

      logger.log("Trip fetch - todo_list:", responseData.todo_list);

      set({
        tripData: responseData.tripData,
        tripInfo: responseData.tripInfo,
        localVersion: responseData.tripData.version || 0,
        tripTodos: responseData.todo_list,
        loading: false,
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        window.history.back();
        toast({
          title: "Access Denied",
          description: "You are not authorized to view this trip.",
          variant: "destructive",
        });
        return;
      }

      set({
        error: err instanceof Error ? err : new Error("An error occurred"),
        loading: false,
        hasAccess: false,
        isParticipant: false,
        lastTripEndDate: null,
      });
    }
  },

  updateTripData: (newData: Partial<Trip>) => {
    set((state) => {
      if (!state.tripData) return state;
      const updatedData: Trip = {
        ...state.tripData,
        ...newData,
        version: (state.tripData.version || 0) + 1,
      };

      const selectedLocationId = state.selectedLocation?.id;

      if (selectedLocationId) {
        const location = updatedData.locations.find(
          (l) => l.id === selectedLocationId
        );
        if (location) {
          const newBudget = calculateLocationBudget(location);
          updatedData.locations = updatedData.locations.map((l) =>
            l.id === selectedLocationId ? { ...l, budget: newBudget } : l
          );
        }
      }

      debouncedUpdateServer(updatedData);

      return {
        tripData: updatedData,
        localVersion: updatedData.version,
        lastTripEndDate: calculateLastTripEndDate(updatedData.locations),
      };
    });
  },

  addLocation: (newLocation: Location) => {
    set((state) => {
      if (!state.tripData) return state;
      const updatedData: Trip = {
        ...state.tripData,
        locations: [...state.tripData.locations, newLocation],
        version: (state.tripData.version || 0) + 1,
      };
      debouncedUpdateServer(updatedData);
      return {
        tripData: updatedData,
        localVersion: updatedData.version,
        lastTripEndDate: calculateLastTripEndDate(updatedData.locations),
      };
    });
  },

  updateLocation: (id: string, updates: Partial<Location>) => {
    set((state) => {
      if (!state.tripData) return state;

      const updatedLocations = state.tripData.locations.map((location) => {
        if (location.id === id) {
          if (updates.participants) {
            const currentParticipants = location.participants || [];
            const addedParticipants = updates.participants.filter(
              (p) => !currentParticipants.some((cp) => cp.id === p.id)
            );
            const removedParticipants = currentParticipants.filter(
              (p) => !updates?.participants?.some((up) => up.id === p.id)
            );

            if (addedParticipants.length > 0) {
              state.createLocationUpdate(
                location.waytora_id,
                location.id,
                "participants_changed",
                {
                  added: true,
                  participants: addedParticipants,
                }
              );
            }

            if (removedParticipants.length > 0) {
              state.createLocationUpdate(
                location.waytora_id,
                location.id,
                "participants_changed",
                {
                  added: false,
                  participants: removedParticipants,
                }
              );
            }
          }

          return {
            ...location,
            ...updates,
          };
        }
        return location;
      });

      const updatedData: Trip = {
        ...state.tripData,
        locations: updatedLocations,
        version: (state.tripData.version || 0) + 1,
      };

      const updatedSelectedLocation =
        state.selectedLocation?.id === id
          ? updatedLocations.find((loc) => loc.id === id) ||
            state.selectedLocation
          : state.selectedLocation;

      get().updateTripData(updatedData);

      return {
        tripData: updatedData,
        localVersion: updatedData.version,
        selectedLocation: updatedSelectedLocation,
        lastTripEndDate: calculateLastTripEndDate(updatedData.locations),
      };
    });
  },

  updateActivityItem: async (locationId, date, activityId, updates) => {
    let updatedTrip: Trip | null = null;
    set((state) => {
      if (!state.tripData) return state;

      const updatedLocations = state.tripData.locations.map((location) => {
        if (location.id === locationId) {
          const updatedItems = location.itinerary_items.map((item) => {
            if (item.date === date) {
              return {
                ...item,
                items: item.items.map((i) =>
                  i.id === activityId ? { ...i, ...updates } : i
                ),
              };
            }
            return item;
          });
          return {
            ...location,
            itinerary_items: updatedItems,
          };
        }
        return location;
      });

      updatedTrip = {
        ...state.tripData,
        locations: updatedLocations,
        version: (state.tripData.version || 0) + 1,
      };

      setTimeout(() => get().recalculateLocationBudget(locationId), 0);

      return {
        ...state,
        tripData: updatedTrip,
        lastTripEndDate: calculateLastTripEndDate(updatedTrip.locations),
      };
    });

    if (updatedTrip) {
      await updateServer(updatedTrip);
    }
  },

  setSelectedLocationId: (locationId) =>
    set({ selectedLocationId: locationId }),

  getSelectedLocation: () => {
    const { tripData, selectedLocationId } = get();
    const location = tripData?.locations.find(
      (location) => location.id === selectedLocationId
    );
    return location || tripData?.locations[0] || null;
  },

  getItineraryItems: () => {
    const { tripData, selectedLocation } = get();
    const location = tripData?.locations.find(
      (location) => location.id === selectedLocation?.id
    );

    return location?.itinerary_items || [];
  },
  getItineraryItemLength: (selectedDay: string, location: Location) => {
    return (
      location?.itinerary_items.find((item) => item.date === selectedDay)?.items
        .length || 0
    );
  },

  getChatItineraryItems: () => {
    const { tripData, selectedLocation } = get();
    const location = tripData?.locations.find(
      (location) => location.id === selectedLocation?.id
    );
    return (
      location?.itinerary_items?.map((item) => ({
        date: item.date,
        items: item.items.map((item) => ({
          id: item.id.toString(),
          name: item.name,
          start_time: item.start_time || "",
        })),
      })) || []
    );
  },

  getAdjacentLocations: () => {
    const { tripData, selectedLocation } = get();
    if (!tripData || !selectedLocation)
      return { previousLocation: null, nextLocation: null };

    const selectedLocationIndex = tripData.locations.findIndex(
      (location) => location.id === selectedLocation.id
    );

    const previousLocation =
      selectedLocationIndex > 0
        ? tripData.locations[selectedLocationIndex - 1]
        : null;

    const nextLocation =
      selectedLocationIndex < tripData.locations.length - 1
        ? tripData.locations[selectedLocationIndex + 1]
        : null;

    return {
      previousLocation: previousLocation ?? null,
      nextLocation: nextLocation ?? null,
    };
  },

  setSelectedDay: (date: string | null) => set({ selectedDay: date }),
  bookingStats: {},
  forceStatsUpdate: 0,
  bookingStatsVersion: 0,
  updateBookingStats: (locationId, stats) =>
    set((state) => ({
      bookingStats: {
        ...state.bookingStats,
        [locationId]: stats,
      },
      bookingStatsVersion: (state.bookingStatsVersion || 0) + 1,
      forceStatsUpdate: state.forceStatsUpdate + 1,
    })),

  recordView: async (tripId: string, duration: number) => {
    try {
      await api.social.post(`/trips/${tripId}/engagement`, {
        type: "view",
        metadata: {
          duration: Math.round(duration),
        },
      });
    } catch (error) {
      console.error("Failed to record trip view:", error);
    }
  },

  recordEngagement: async (
    tripId: string,
    type: "view" | "like" | "share" | "unlike",
    metadata: any
  ) => {
    try {
      await api.social.post(`/trips/${tripId}/engagement`, {
        type,
        metadata,
      });
    } catch (error) {
      console.error("Failed to record engagement:", error);
    }
  },

  copyTrip: async (tripId: string) => {
    try {
      await api.social.post(`/trips/${tripId}/copy`);
    } catch (error) {
      console.error("Failed to copy trip:", error);
    }
  },

  createLocationUpdate: async (waytoraId, locationId, type, metadata) => {
    if (!waytoraId.startsWith("w-")) {
      logger.log("Invalid location ID:", waytoraId);
      return;
    }

    const { tripData, selectedLocation } = get();
    if (!tripData || !selectedLocation) return;

    const location = tripData.locations.find((loc) => loc.id === locationId);
    if (!location) return;

    let metadataObj: UpdateMetadata;
    switch (type) {
      case "experience_added":
        metadataObj = metadata as ExperienceMetadata;
        break;
      case "experience_removed":
        metadataObj = metadata as ExperienceMetadata;
        break;
      case "accommodation_added":
        metadataObj = metadata as AccommodationMetadata;
        break;
      case "transport_added":
        metadataObj = metadata as TransportMetadata;
        break;
      case "participants_changed":
        metadataObj = metadata as ParticipantsMetadata;
        break;
      default:
        return;
    }

    try {
      const response = await api.social.post("/location-updates", {
        waytora_id: waytoraId,
        location_id: locationId,
        trip_id: tripData.id,
        type,
        user_id: useAuthStore.getState().user?.user_id,
        created_at: new Date().toISOString(),
        metadata: metadataObj,
      });

      if (response.status !== 200)
        throw new Error("Failed to create location update");
    } catch (error) {
      console.error("Failed to create location update:", error);
    }
  },

  checkIsAdmin: () => {
    const { tripData, hasAccess, isParticipant } = get();
    if (!tripData || !hasAccess || !isParticipant) return false;

    const user = useAuthStore.getState().user;
    if (!user) return false;

    const currentUser = tripData.users.find((u) => u.user_id === user.user_id);
    return currentUser?.is_admin ?? false;
  },

  requestAdminAccess: async () => {
    const { tripData } = get();
    if (!tripData) return;

    try {
      await api.social.post(`/trips/${tripData.id}/request-admin`);
      toast({
        title: "Request Sent",
        description:
          "Admin access request has been sent to trip administrators.",
      });
    } catch (error) {
      console.error("Failed to request admin access:", error);
      toast({
        title: "Error",
        description: "Failed to send admin access request. Please try again.",
        variant: "destructive",
      });
    }
  },

  isPermissionDialogOpen: false,
  setPermissionDialogOpen: (isOpen) => set({ isPermissionDialogOpen: isOpen }),

  recalculateLocationBudget: (locationId: string) => {
    set((state) => {
      if (!state.tripData) return state;

      const updatedLocations = state.tripData.locations.map((location) => {
        if (location.id === locationId) {
          const newBudget = calculateLocationBudget(location);
          return {
            ...location,
            budget: newBudget,
          };
        }
        return location;
      });

      const updatedTrip = {
        ...state.tripData,
        locations: updatedLocations,
        version: (state.tripData.version || 0) + 1,
      };

      const selectedLocationBudget =
        state.selectedLocation?.id === locationId
          ? updatedLocations.find((l) => l.id === locationId)?.budget ||
            state.selectedLocationBudget
          : state.selectedLocationBudget;

      return {
        tripData: updatedTrip,
        selectedLocationBudget,
        lastTripEndDate: calculateLastTripEndDate(updatedTrip.locations),
      };
    });
  },

  getLocationBudgetBreakdown: (locationId: string) => {
    const state = get();
    const location = state.tripData?.locations.find((l) => l.id === locationId);

    if (!location || !state.tripData) {
      return {
        accommodation: [],
        transport: [],
        activities: [],
      };
    }

    const accommodation = location.accommodation.main
      ? [
          {
            name: location.accommodation.main.name,
            cost: location.accommodation.main.pricing?.amount || 0,
            image: location.accommodation.main.mainImageURL || undefined,
            duration: location.accommodation.main.duration,
          },
        ]
      : [];

    const transport = state.tripData.transport
      .filter((t) => t.toLocationId === locationId)
      .map((transport) => ({
        name: `Flight from ${transport.legs[0]?.departure.location.name} to ${
          transport.legs[transport.legs.length - 1]?.arrival.location.name
        }`,
        cost: transport.total_price,
        image: transport.legs[0]?.provider.logo || undefined,
      }));

    const activities = location.itinerary_items.flatMap((day) =>
      day.items.map((item) => ({
        name: item.name,
        cost: item.pricing || 0,
        image: item.images?.[0] || undefined,
        participants: item.participants?.filter((p) => p.status === "confirmed")
          .length,
      }))
    );

    return {
      accommodation,
      transport,
      activities,
    };
  },

  getLocationBudgetPerParticipant: (locationId: string) => {
    const state = get();
    const location = state.tripData?.locations.find((l) => l.id === locationId);
    if (!location) {
      return [];
    }
    const accommodationCost = location.accommodation.main?.pricing?.total || 0;
    const numParticipants = location.participants.length || 1;
    const accommodationShare = accommodationCost / numParticipants;

    const transportCost = calculateTransportCost(state.tripData, locationId);
    const transportShare = transportCost;

    const activityShares: Record<string, number> = {};
    location.itinerary_items.forEach((day) => {
      day.items.forEach((item) => {
        if (
          item.participants &&
          item.participants.filter((p) => p.status === "confirmed").length > 0
        ) {
          const confirmedParticipants = item.participants.filter(
            (p) => p.status === "confirmed"
          );
          const share = item.pricing || 0;
          confirmedParticipants.forEach((participant) => {
            activityShares[participant.id] =
              (activityShares[participant.id] || 0) + share;
          });
        }
      });
    });

    return location.participants.map((p) => {
      const activitiesShare = activityShares[p.id] || 0;
      return {
        participant: { ...p, waytora_avatar_url: p.profile_image },
        accommodationShare,
        transportShare,
        activitiesShare,
        total: accommodationShare + transportShare + activitiesShare,
      };
    });
  },

  updateAccommodationItem: async (
    locationId: string,
    updates: Partial<TripLodging>
  ) => {
    let updatedTrip: Trip | null = null;
    set((state) => {
      if (!state.tripData) return state;

      const updatedLocations = state.tripData.locations.map((location) => {
        if (location.id === locationId && location.accommodation.main) {
          return {
            ...location,
            accommodation: {
              ...location.accommodation,
              main: {
                ...location.accommodation.main,
                ...updates,
              },
            },
          };
        }
        return location;
      });

      updatedTrip = {
        ...state.tripData,
        locations: updatedLocations,
        version: (state.tripData.version || 0) + 1,
      };

      return {
        ...state,
        tripData: updatedTrip,
        lastTripEndDate: calculateLastTripEndDate(updatedTrip.locations),
      };
    });

    if (updatedTrip) {
      get().updateTripData(updatedTrip);
    }
  },

  resetState: () => {
    debouncedUpdateServer.cancel();

    set({
      tripData: null,
      tripInfo: null,
      tripTodos: [],
      selectedLocation: null,
      selectedLocationId: null,
      lastTripEndDate: null,
      loading: false,
      error: null,
      localVersion: 0,
      hasAccess: false,
      isParticipant: false,
      selectedDay: null,
      previousLocation: null,
      nextLocation: null,
      bookingStats: {},
      forceStatsUpdate: 0,
      bookingStatsVersion: 0,
      isPermissionDialogOpen: false,
      selectedLocationBudget: {
        total: 0,
        accommodation: 0,
        transport: 0,
        activities: 0,
      },
    });
  },

  addExperienceToItinerary: async (
    experience: WaytoraExperienceInitial,
    selectedDay: string,
    selectedLocationId: string,
    placeLocationDetails?: {
      address: string;
      latitude: number;
      longitude: number;
    } | null
  ) => {
    const { tripData, createLocationUpdate } = get();

    const selectedLocation = tripData?.locations.find(
      (l) => l.id === selectedLocationId
    );

    if (!selectedDay || !selectedLocation || !tripData) {
      console.error(
        "Selected day, location, or trip data is not defined for adding experience."
      );
      toast({
        title: "Error Adding Experience",
        description: "Could not find the necessary trip details.",
        variant: "destructive",
      });
      return;
    }

    try {
      let fetchedDetails = placeLocationDetails;
      if (!fetchedDetails) {
        // fetch
        fetchedDetails = await getCoordinatesFromPlaceId(
          experience.waytora_place_id,
          selectedLocation,
          experience.name
        );
      }

      const imageUrl = experience.images?.[0]?.url || "";
      const allImages = experience.images || [];

      // Get the latest trip data and selected location after any async operations complete
      const latestTripData = get().tripData;
      if (!latestTripData) {
        throw new Error("Trip data is no longer available");
      }

      const latestSelectedLocation = latestTripData.locations.find(
        (l) => l.id === selectedLocationId
      );

      if (!latestSelectedLocation) {
        throw new Error("Selected location is no longer available");
      }

      // Use the latest data to find the day itinerary
      const latestItineraryItems = [...latestSelectedLocation.itinerary_items];
      const dayItinerary = latestItineraryItems.find(
        (item) =>
          item.date &&
          new Date(item.date).toDateString() ===
            new Date(selectedDay).toDateString()
      );

      const newStartTime = dayItinerary
        ? await calculateStartTime(
            {
              date: selectedDay,
              items: dayItinerary.items,
            },
            latestSelectedLocation.date.time_zone || "UTC"
          )
        : new Date("09:00").toISOString();

      const newItineraryItem: ItineraryItem = {
        name: experience.name,
        start_time: newStartTime,
        duration:
          experience.time_to_spend > 1 ? experience.time_to_spend - 1 : 1,
        images: [imageUrl],
        coordinates: {
          latitude: fetchedDetails.latitude,
          longitude: fetchedDetails.longitude,
        },
        metadata: {
          description: experience.description,
          tip: experience.tips,
        },
        pricing: 0,
        id: experience.id.toString(),
        sourceId: {
          waytoraPlaceId: experience.waytora_place_id,
        },
        type: "waytora",
        bookingStatus: "confirmed" as BookingStatus,
        categories: experience.types,
        participants: null,
      };

      // Update the state using the functional updater to avoid race conditions
      set((state) => {
        if (!state.tripData) return state;

        // Get the most current itinerary items for this location
        const currentLocation = state.tripData.locations.find(
          (l) => l.id === selectedLocationId
        );

        if (!currentLocation) return state;

        const currentItineraryItems = [...currentLocation.itinerary_items];
        let currentDayItinerary = currentItineraryItems.find(
          (item) =>
            item.date &&
            new Date(item.date).toDateString() ===
              new Date(selectedDay).toDateString()
        );

        if (currentDayItinerary) {
          // Update existing day
          const dayIndex = currentItineraryItems.findIndex(
            (item) =>
              item.date &&
              new Date(item.date).toDateString() ===
                new Date(selectedDay).toDateString()
          );

          if (dayIndex > -1) {
            currentItineraryItems[dayIndex] = {
              ...currentDayItinerary,
              items: [...currentDayItinerary.items, newItineraryItem],
            };
          }
        } else {
          // Create new day
          currentDayItinerary = {
            date: selectedDay,
            items: [newItineraryItem],
          };
          currentItineraryItems.push(currentDayItinerary);
        }

        // Create updated location with new itinerary items
        const updatedLocation = {
          ...currentLocation,
          itinerary_items: currentItineraryItems,
        };

        // Update locations array with the updated location
        const updatedLocations = state.tripData.locations.map((location) =>
          location.id === selectedLocationId ? updatedLocation : location
        );

        // Return updated trip data
        return {
          tripData: {
            ...state.tripData,
            locations: updatedLocations,
            version: (state.tripData.version || 0) + 1,
          },
        };
      });

      // Create location update (async, happens after local state update)
      await createLocationUpdate(
        selectedLocation.waytora_id,
        selectedLocation.id,
        "experience_added",
        {
          itemName: experience.name,
          itemType: "experience",
          duration: experience.time_to_spend,
          images: allImages.map((img) => img.url), // Pass image URLs
        }
      );

      // Trigger server update via debouncedUpdateServer
      const finalTripData = get().tripData;
      if (finalTripData) {
        debouncedUpdateServer(finalTripData);
      }
    } catch (error) {
      console.error("Error adding experience to itinerary:", error);
      toast({
        title: "Error",
        description: "Failed to add experience. Please try again.",
        variant: "destructive",
      });
    }
  },

  addViatorExperienceToItinerary: async (
    product: ViatorProductInitial,
    selectedDay: string,
    selectedLocationId: string
  ) => {
    const { tripData, createLocationUpdate } = get();

    const selectedLocation = tripData?.locations.find(
      (l) => l.id === selectedLocationId
    );

    if (!selectedDay || !selectedLocation || !tripData) {
      console.error(
        "Selected day, location, or trip data is not defined for adding experience."
      );
      toast({
        title: "Error Adding Experience",
        description: "Could not find the necessary trip details.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Using existing API via the utility function
      const productDetails = await getMoreProductDetails(
        product.product_code,
        selectedLocation.viator_id
      );

      if (!productDetails) {
        throw new Error("Failed to get product details");
      }

      // Get the latest trip data after the async operation
      const latestTripData = get().tripData;
      if (!latestTripData) {
        throw new Error("Trip data is no longer available");
      }

      const latestSelectedLocation = latestTripData.locations.find(
        (l) => l.id === selectedLocationId
      );

      const user = useAuthStore.getState().user;

      if (!user) {
        throw new Error("User is not logged in");
      }

      const tripUser: TripUser = {
        user_id: user.user_id,
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username,
        profile_image: user.profile_image,
        is_admin: true,
        creator:
          tripData.users.find((u) => u.user_id === user.user_id)?.creator ||
          false,
      };

      await addViatorProductToDay(
        productDetails,
        product,
        tripData,
        selectedLocation,
        selectedDay,
        tripUser
      );

      // Create location update
      await createLocationUpdate(
        selectedLocation.waytora_id,
        selectedLocation.id,
        "experience_added",
        {
          itemName: productDetails.name,
          itemType: "experience",
          duration: productDetails.duration,
          images: productDetails.images.map((image) => image.highRes),
          rating: productDetails.rating,
        }
      );

      // Trigger server update
      const finalTripData = get().tripData;
      if (finalTripData) {
        debouncedUpdateServer(finalTripData);
      }

      // Recalculate location budget
      get().recalculateLocationBudget(selectedLocationId);
    } catch (error) {
      console.error("Error adding Viator experience to itinerary:", error);
      toast({
        title: "Error",
        description: "Failed to add experience. Please try again.",
        variant: "destructive",
      });
    }
  },

  getMoreProductDetails: async (productCode: string, viatorId: string) => {
    // Implementation of getMoreProductDetails method
    // This is a placeholder and should be replaced with the actual implementation
    throw new Error("Method not implemented");
  },
}));

const updateServer = async (trip: Trip) => {
  const tripInfo = useTripStore.getState().tripInfo;

  try {
    await api.trips.put(`/${trip.id}`, {
      tripData: trip,
      tripInfo: tripInfo,
      currency: useCurrencyStore.getState().selectedCurrency.code,
    });
  } catch (error: any) {
    console.error("Failed to update server:", error);
    logger.log("Failed update details:", {
      tripId: trip.id,
      version: trip.version,
      error: error.message,
    });
    throw error;
  }
};

const debouncedUpdateServer = debounce(updateServer, 500);

const calculateLocationBudget = (location: Location): LocationBudget => {
  if (location.origin)
    return {
      accommodation: 0,
      transport: 0,
      activities: 0,
      total: 0,
    };

  const accommodationCost = location.accommodation.main?.pricing?.total || 0;
  const activitiesCost = calculateActivitiesCost(location.itinerary_items);
  const transportCost =
    calculateTransportCost(useTripStore.getState().tripData, location.id) *
    location.participants.length;

  return {
    accommodation: accommodationCost,
    transport: transportCost,
    activities: activitiesCost,
    total: accommodationCost + activitiesCost + transportCost,
  };
};

const calculateActivitiesCost = (
  items: { date: string; items: ItineraryItem[] }[]
): number => {
  return items.reduce((total, day) => {
    return (
      total +
      day.items.reduce((dayTotal, item) => {
        const participantCount = item.participants
          ? item.participants.filter((p) => p.status === "confirmed").length
          : 1;
        return dayTotal + (item.pricing || 0) * participantCount;
      }, 0)
    );
  }, 0);
};

const calculateTransportCost = (
  tripData: Trip | null,
  locationId: string
): number => {
  if (!tripData) return 0;
  return tripData.transport.reduce((total, transport) => {
    if (transport.toLocationId === locationId) {
      return total + (transport.total_price || 0);
    }
    return total;
  }, 0);
};

const calculateLastTripEndDate = (
  locations: Location[] | undefined | null
): Date | null => {
  if (!locations || locations.length === 0) {
    return null;
  }
  const relevantLocations = locations.filter((loc) => !loc.origin);
  const lastLocation =
    relevantLocations.length > 0
      ? relevantLocations[relevantLocations.length - 1]
      : locations[locations.length - 1];

  if (!lastLocation?.date?.end_date) {
    return null;
  }
  try {
    const endDate = new Date(lastLocation.date.end_date);
    if (!isNaN(endDate.getTime())) {
      return endDate;
    }
  } catch (e) {
    console.error("Error parsing last location end date:", e);
  }
  return null;
};

export default useTripStore;
